import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Card,
  CardContent,
  useTheme,
  alpha,
  Tooltip,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Collapse,
} from "@mui/material";
import {
  PlayArrow as GenerateIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  Description as InvoiceIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
  AccountBalance as TaxIcon,
  Euro as EuroIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Schedule as ScheduleIcon,
  Assignment as TaskIcon,
  Payment as PaymentIcon,
  MoneyOff as UnpaidIcon,
  AttachMoney as MoneyIcon,
  DateRange as CalendarIcon,
  CheckCircleOutline as TodoIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import api from "../services/api";

const currentYear = new Date().getFullYear();
const yearOptions = [
  currentYear,
  currentYear - 1,
  currentYear - 2,
  currentYear - 3,
];

export default function DirectorReviewPage() {
  const theme = useTheme();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [accountingBasis, setAccountingBasis] = useState("accrual");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [expandedExpense, setExpandedExpense] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/accounts/generate?year=${selectedYear}&basis=${accountingBasis}`
      );
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch accounts:", err);
      setError(err.response?.data?.error || "Failed to load financial data");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, accountingBasis]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate verification checks
  const getVerificationStatus = () => {
    if (!data) return { passed: 0, total: 0, issues: [] };

    const checks = [];
    const issues = [];

    // Check 1: Invoice totals match
    const invoiceTotal =
      data.transactions?.sales?.reduce((sum, inv) => sum + inv.netAmount, 0) ||
      0;
    const reportedRevenue = data.summary?.revenue || 0;
    checks.push({
      name: "Invoice totals match reported revenue",
      passed: Math.abs(invoiceTotal - reportedRevenue) < 0.01,
      expected: reportedRevenue,
      actual: invoiceTotal,
    });
    if (Math.abs(invoiceTotal - reportedRevenue) >= 0.01) {
      issues.push(
        `Invoice total (${formatMoney(
          invoiceTotal
        )}) doesn't match revenue (${formatMoney(reportedRevenue)})`
      );
    }

    // Check 2: Expense totals match
    const expenseTotal =
      data.transactions?.expenses?.reduce(
        (sum, exp) => sum + (exp.netAmount || exp.amount || 0),
        0
      ) || 0;
    const reportedExpenses = data.summary?.expenses || 0;
    checks.push({
      name: "Expense totals match reported expenses",
      passed: Math.abs(expenseTotal - reportedExpenses) < 0.01,
      expected: reportedExpenses,
      actual: expenseTotal,
    });
    if (Math.abs(expenseTotal - reportedExpenses) >= 0.01) {
      issues.push(
        `Expense total (${formatMoney(
          expenseTotal
        )}) doesn't match reported (${formatMoney(reportedExpenses)})`
      );
    }

    // Check 3: VAT collected matches
    const vatFromInvoices =
      data.transactions?.sales?.reduce(
        (sum, inv) => sum + (inv.vatAmount || 0),
        0
      ) || 0;
    const reportedVatCollected = data.vatSummary?.vatCollected || 0;
    checks.push({
      name: "VAT collected matches invoice VAT",
      passed: Math.abs(vatFromInvoices - reportedVatCollected) < 0.01,
      expected: reportedVatCollected,
      actual: vatFromInvoices,
    });

    // Check 4: Profit calculation
    const calculatedProfit =
      reportedRevenue - reportedExpenses - (data.summary?.staffCosts || 0);
    const reportedProfit = data.summary?.operatingProfit || 0;
    checks.push({
      name: "Profit calculation is correct",
      passed: Math.abs(calculatedProfit - reportedProfit) < 0.01,
      expected: reportedProfit,
      actual: calculatedProfit,
    });

    return {
      passed: checks.filter((c) => c.passed).length,
      total: checks.length,
      checks,
      issues,
    };
  };

  const verification = data ? getVerificationStatus() : null;

  // Export to CSV
  const exportToCSV = (type) => {
    if (!data) return;

    let csvContent = "";
    let filename = "";

    if (type === "invoices") {
      filename = `invoices_${selectedYear}.csv`;
      csvContent =
        "Invoice #,Date,Client,Net Amount,VAT,Gross Amount,Status,Currency\n";
      data.transactions?.sales?.forEach((inv) => {
        csvContent += `"${inv.invoiceNumber}","${formatDate(inv.date)}","${
          inv.clientName || ""
        }",${inv.netAmount},${inv.vatAmount},${inv.grossAmount},"${
          inv.status
        }","${inv.originalCurrency || "EUR"}"\n`;
      });
    } else if (type === "expenses") {
      filename = `expenses_${selectedYear}.csv`;
      csvContent = "Date,Description,Category,Net Amount,VAT,Gross Amount\n";
      data.transactions?.expenses?.forEach((exp) => {
        csvContent += `"${formatDate(exp.date)}","${
          exp.description || exp.fileName || ""
        }","${exp.category}",${exp.netAmount || exp.amount},${
          exp.vatAmount || 0
        },${exp.grossAmount || exp.amount}\n`;
      });
    } else if (type === "summary") {
      filename = `financial_summary_${selectedYear}.csv`;
      csvContent = "Item,Amount\n";
      csvContent += `Total Revenue,${data.summary?.revenue}\n`;
      csvContent += `Total Expenses,${data.summary?.expenses}\n`;
      csvContent += `Staff Costs,${data.summary?.staffCosts}\n`;
      csvContent += `Operating Profit,${data.summary?.operatingProfit}\n`;
      csvContent += `Corporation Tax (12.5%),${data.summary?.corporationTax}\n`;
      csvContent += `Net Profit After Tax,${data.summary?.netProfit}\n`;
      csvContent += `VAT Collected,${data.vatSummary?.vatCollected}\n`;
      csvContent += `VAT Paid,${data.vatSummary?.vatPaid}\n`;
      csvContent += `VAT Liability,${data.vatSummary?.vatLiability}\n`;
    }

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  // Summary Card Component
  const SummaryCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
    verified,
  }) => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border:
          verified === false
            ? `2px solid ${theme.palette.error.main}`
            : undefined,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette[color].main, 0.1),
            }}
          >
            <Icon sx={{ color: theme.palette[color].main, fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {verified !== undefined && (
            <Tooltip title={verified ? "Verified ✓" : "Needs review"}>
              {verified ? (
                <CheckIcon color="success" />
              ) : (
                <WarningIcon color="error" />
              )}
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  // Verification Banner
  const VerificationBanner = () => {
    if (!verification) return null;
    const allPassed = verification.passed === verification.total;

    return (
      <Alert
        severity={allPassed ? "success" : "warning"}
        icon={allPassed ? <VerifiedIcon /> : <WarningIcon />}
        sx={{ mb: 3, borderRadius: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={fetchData}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        }
      >
        <AlertTitle>
          {allPassed ? "All Verification Checks Passed" : "Review Required"}
        </AlertTitle>
        {allPassed ? (
          `${verification.passed}/${verification.total} checks passed. Your financial data is consistent and ready for Revenue submission.`
        ) : (
          <>
            {verification.passed}/{verification.total} checks passed.
            {verification.issues.map((issue, i) => (
              <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
                • {issue}
              </Typography>
            ))}
          </>
        )}
      </Alert>
    );
  };

  // Main render
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" fontWeight={800} color="white">
              Director's Financial Review
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.8)">
              Verify and reconcile your financial data before Revenue submission
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl
              size="small"
              sx={{ minWidth: 100, bgcolor: "white", borderRadius: 1 }}
            >
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 120, bgcolor: "white", borderRadius: 1 }}
            >
              <Select
                value={accountingBasis}
                onChange={(e) => setAccountingBasis(e.target.value)}
              >
                <MenuItem value="accrual">Accrual</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          {/* Verification Status */}
          <VerificationBanner />

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Total Revenue"
                value={formatMoney(data.summary?.revenue)}
                subtitle={`${data.counts?.invoices || 0} invoices`}
                icon={RevenueIcon}
                color="success"
                verified={verification?.checks?.[0]?.passed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Total Expenses"
                value={formatMoney(data.summary?.expenses)}
                subtitle={`${data.counts?.expenses || 0} receipts`}
                icon={ExpenseIcon}
                color="error"
                verified={verification?.checks?.[1]?.passed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Operating Profit"
                value={formatMoney(data.summary?.operatingProfit)}
                subtitle={`${data.metrics?.profitMargin || 0}% margin`}
                icon={TaxIcon}
                color="primary"
                verified={verification?.checks?.[3]?.passed}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="VAT Position"
                value={formatMoney(
                  Math.abs(data.vatSummary?.vatLiability || 0)
                )}
                subtitle={
                  data.vatSummary?.vatLiability >= 0
                    ? "Payable to Revenue"
                    : "Refund due"
                }
                icon={EuroIcon}
                color={data.vatSummary?.vatLiability >= 0 ? "warning" : "info"}
                verified={verification?.checks?.[2]?.passed}
              />
            </Grid>
          </Grid>

          {/* Year-End Action Center */}
          <YearEndActionCenter
            data={data}
            selectedYear={selectedYear}
            formatMoney={formatMoney}
            theme={theme}
          />

          {/* Tabs for detailed views */}
          <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
            >
              <Tab
                label={
                  <Badge badgeContent={data.counts?.invoices} color="success">
                    <Box sx={{ pr: 2 }}>Invoices</Box>
                  </Badge>
                }
              />
              <Tab
                label={
                  <Badge badgeContent={data.counts?.expenses} color="error">
                    <Box sx={{ pr: 2 }}>Expenses</Box>
                  </Badge>
                }
              />
              <Tab label="VAT Reconciliation" />
              <Tab label="Verification Checks" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Invoices Tab */}
              {activeTab === 0 && (
                <InvoicesTab
                  data={data}
                  formatMoney={formatMoney}
                  formatDate={formatDate}
                  expandedInvoice={expandedInvoice}
                  setExpandedInvoice={setExpandedInvoice}
                  exportToCSV={exportToCSV}
                  theme={theme}
                />
              )}

              {/* Expenses Tab */}
              {activeTab === 1 && (
                <ExpensesTab
                  data={data}
                  formatMoney={formatMoney}
                  formatDate={formatDate}
                  expandedExpense={expandedExpense}
                  setExpandedExpense={setExpandedExpense}
                  exportToCSV={exportToCSV}
                  theme={theme}
                />
              )}

              {/* VAT Tab */}
              {activeTab === 2 && (
                <VATTab data={data} formatMoney={formatMoney} theme={theme} />
              )}

              {/* Verification Tab */}
              {activeTab === 3 && (
                <VerificationTab
                  verification={verification}
                  formatMoney={formatMoney}
                  theme={theme}
                />
              )}
            </Box>
          </Paper>

          {/* Export Section */}
          <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Export for Accountant
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download CSV files to share with your accountant or for your
              records.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => exportToCSV("summary")}
              >
                Financial Summary
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => exportToCSV("invoices")}
              >
                All Invoices
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => exportToCSV("expenses")}
              >
                All Expenses
              </Button>
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}

// Year-End Action Center Component
function YearEndActionCenter({ data, selectedYear, formatMoney, theme }) {
  const navigate = useNavigate();

  // Calculate key year-end metrics
  const unpaidCount = data.unpaidInvoices?.count || 0;
  const unpaidTotal = data.unpaidInvoices?.total || 0;
  const corpTax = data.profitAndLoss?.corporationTax || 0;
  const vatLiability = data.vatSummary?.vatLiability || 0;
  const operatingProfit = data.summary?.operatingProfit || 0;

  // Calculate filing deadline (9 months after financial year end - typically Dec 31)
  const yearEnd = new Date(selectedYear, 11, 31); // Dec 31
  const filingDeadline = new Date(selectedYear + 1, 8, 21); // Sept 21 following year (9 months + 21 days)
  const paymentDeadline = new Date(selectedYear + 1, 8, 21); // Same for small companies
  const today = new Date();
  const daysToFiling = Math.ceil(
    (filingDeadline - today) / (1000 * 60 * 60 * 24)
  );

  // Aging breakdown
  const aging = data.unpaidInvoices?.aging || {};
  const overdueAmount =
    (aging["1-30"] || 0) +
    (aging["31-60"] || 0) +
    (aging["61-90"] || 0) +
    (aging["90+"] || 0);

  return (
    <Paper
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <TaskIcon color="primary" />
        <Typography variant="h6" fontWeight={700}>
          Year-End Action Center - {selectedYear}
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        {/* Corporation Tax Card */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: "100%",
              bgcolor: alpha(theme.palette.warning.main, 0.05),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <TaxIcon color="warning" />
              <Typography variant="subtitle1" fontWeight={700}>
                Corporation Tax Due
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              fontWeight={800}
              color="warning.dark"
              sx={{ mb: 1 }}
            >
              {formatMoney(corpTax)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              12.5% on taxable profit of{" "}
              {formatMoney(Math.max(0, operatingProfit))}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Filing Deadline
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {filingDeadline.toLocaleDateString("en-IE", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Payment Due
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {paymentDeadline.toLocaleDateString("en-IE", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </Typography>
              </Stack>
              {daysToFiling > 0 && (
                <Chip
                  icon={<CalendarIcon />}
                  label={`${daysToFiling} days remaining`}
                  size="small"
                  color={
                    daysToFiling < 30
                      ? "error"
                      : daysToFiling < 90
                      ? "warning"
                      : "success"
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Unpaid Invoices Card */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: "100%",
              bgcolor:
                unpaidCount > 0
                  ? alpha(theme.palette.error.main, 0.05)
                  : alpha(theme.palette.success.main, 0.05),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <UnpaidIcon color={unpaidCount > 0 ? "error" : "success"} />
              <Typography variant="subtitle1" fontWeight={700}>
                Unpaid Invoices
              </Typography>
            </Stack>
            {unpaidCount > 0 ? (
              <>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color="error.dark"
                  sx={{ mb: 1 }}
                >
                  {formatMoney(unpaidTotal)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {unpaidCount} invoice{unpaidCount !== 1 ? "s" : ""}{" "}
                  outstanding
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Aging Breakdown:
                </Typography>
                <Stack spacing={0.5}>
                  {aging.current > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">
                        Current (not due)
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(aging.current)}
                      </Typography>
                    </Stack>
                  )}
                  {aging["1-30"] > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="warning.main">
                        1-30 days overdue
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(aging["1-30"])}
                      </Typography>
                    </Stack>
                  )}
                  {aging["31-60"] > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="warning.dark">
                        31-60 days overdue
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(aging["31-60"])}
                      </Typography>
                    </Stack>
                  )}
                  {(aging["61-90"] > 0 || aging["90+"] > 0) && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="error.main">
                        60+ days overdue
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatMoney(
                          (aging["61-90"] || 0) + (aging["90+"] || 0)
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                {overdueAmount > 0 && (
                  <Alert severity="warning" sx={{ mt: 2, py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Action:</strong> Chase{" "}
                      {formatMoney(overdueAmount)} in overdue invoices
                    </Typography>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  color="success.dark"
                  sx={{ mb: 1 }}
                >
                  All Paid! ✓
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No outstanding invoices for {selectedYear}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* VAT & Quick Actions Card */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              height: "100%",
              bgcolor: alpha(theme.palette.info.main, 0.05),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <EuroIcon color="info" />
              <Typography variant="subtitle1" fontWeight={700}>
                VAT Position
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              fontWeight={800}
              color={vatLiability >= 0 ? "error.dark" : "success.dark"}
              sx={{ mb: 1 }}
            >
              {formatMoney(Math.abs(vatLiability))}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {vatLiability >= 0
                ? "Payable to Revenue"
                : "Refund due from Revenue"}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              VAT Summary:
            </Typography>
            <Stack spacing={0.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Output VAT (charged)</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatMoney(data.vatSummary?.vatCollected || 0)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Input VAT (reclaimable)</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatMoney(data.vatSummary?.vatPaid || 0)}
                </Typography>
              </Stack>
            </Stack>
            <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
              <Typography variant="body2">
                VAT3 returns are filed bi-monthly. Ensure all periods are
                submitted.
              </Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Year-End Checklist */}
      <Paper
        sx={{ p: 2.5, mt: 3, borderRadius: 2, bgcolor: "background.paper" }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          📋 Year-End Checklist
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon
                  color={unpaidCount === 0 ? "success" : "action"}
                  fontSize="small"
                />
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: unpaidCount === 0 ? "line-through" : "none",
                  }}
                >
                  Chase all outstanding invoices
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Ensure all expenses/receipts are uploaded
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Review expense categorization for accuracy
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Reconcile bank balance with accounts
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Set aside funds for Corporation Tax ({formatMoney(corpTax)})
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Consider pension contributions (reduces taxable profit)
                </Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 1,
                    mx: -0.5,
                    px: 0.5,
                  },
                }}
                onClick={() => navigate("/dashboard/payroll")}
              >
                <TodoIcon color="action" fontSize="small" />
                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{ textDecoration: "underline" }}
                >
                  Review director salary for tax efficiency →
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TodoIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  Export accounts for your accountant
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </Paper>
  );
}

// Invoices Tab Component
function InvoicesTab({
  data,
  formatMoney,
  formatDate,
  expandedInvoice,
  setExpandedInvoice,
  exportToCSV,
  theme,
}) {
  const [orderBy, setOrderBy] = React.useState("date");
  const [order, setOrder] = React.useState("desc");

  const rawInvoices = data.transactions?.sales || [];
  const totalNet = rawInvoices.reduce((sum, inv) => sum + inv.netAmount, 0);
  const totalVat = rawInvoices.reduce(
    (sum, inv) => sum + (inv.vatAmount || 0),
    0
  );
  const totalGross = rawInvoices.reduce((sum, inv) => sum + inv.grossAmount, 0);

  // Sort invoices based on current sort settings
  const invoices = React.useMemo(() => {
    const sorted = [...rawInvoices].sort((a, b) => {
      let aVal, bVal;
      switch (orderBy) {
        case "invoiceNumber":
          aVal = parseInt(a.invoiceNumber) || 0;
          bVal = parseInt(b.invoiceNumber) || 0;
          break;
        case "date":
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
          break;
        case "client":
          aVal = (a.clientName || "").toLowerCase();
          bVal = (b.clientName || "").toLowerCase();
          break;
        case "netAmount":
          aVal = a.netAmount || 0;
          bVal = b.netAmount || 0;
          break;
        case "grossAmount":
          aVal = a.grossAmount || 0;
          bVal = b.grossAmount || 0;
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        default:
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
      }
      if (typeof aVal === "string") {
        return order === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return order === "asc" ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [rawInvoices, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Invoice Breakdown ({invoices.length} invoices)
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => exportToCSV("invoices")}
        >
          Export CSV
        </Button>
      </Stack>

      <TableContainer sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "invoiceNumber"}
                  direction={orderBy === "invoiceNumber" ? order : "asc"}
                  onClick={() => handleSort("invoiceNumber")}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "date"}
                  direction={orderBy === "date" ? order : "asc"}
                  onClick={() => handleSort("date")}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "client"}
                  direction={orderBy === "client" ? order : "asc"}
                  onClick={() => handleSort("client")}
                >
                  Client
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "netAmount"}
                  direction={orderBy === "netAmount" ? order : "asc"}
                  onClick={() => handleSort("netAmount")}
                >
                  Net (EUR)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                VAT
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "grossAmount"}
                  direction={orderBy === "grossAmount" ? order : "asc"}
                  onClick={() => handleSort("grossAmount")}
                >
                  Gross
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Currency
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow
                key={inv.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)
                }
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {inv.invoiceNumber}
                  </Typography>
                </TableCell>
                <TableCell>{formatDate(inv.date)}</TableCell>
                <TableCell>{inv.clientName || "—"}</TableCell>
                <TableCell align="right">
                  {formatMoney(inv.netAmount)}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(inv.vatAmount)}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {formatMoney(inv.grossAmount)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={inv.status}
                    size="small"
                    color={inv.status === "paid" ? "success" : "warning"}
                    sx={{ textTransform: "capitalize" }}
                  />
                </TableCell>
                <TableCell align="center">
                  {inv.originalCurrency && inv.originalCurrency !== "EUR" ? (
                    <Tooltip
                      title={`Original: ${
                        inv.originalCurrency
                      } ${inv.originalGrossAmount?.toFixed(
                        2
                      )} @ rate ${inv.exchangeRate?.toFixed(4)}`}
                    >
                      <Chip
                        label={inv.originalCurrency}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </Tooltip>
                  ) : (
                    <Chip label="EUR" size="small" variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {/* Totals Row */}
            <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                TOTAL
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalNet)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalVat)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalGross)}
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Verification Note */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Verify:</strong> Total Net ({formatMoney(totalNet)}) should
        match your Revenue figure of {formatMoney(data.summary?.revenue)}
        {Math.abs(totalNet - (data.summary?.revenue || 0)) < 0.01 ? (
          <CheckIcon color="success" sx={{ ml: 1, verticalAlign: "middle" }} />
        ) : (
          <WarningIcon color="error" sx={{ ml: 1, verticalAlign: "middle" }} />
        )}
      </Alert>
    </Box>
  );
}

// Expenses Tab Component
function ExpensesTab({
  data,
  formatMoney,
  formatDate,
  expandedExpense,
  setExpandedExpense,
  exportToCSV,
  theme,
}) {
  const expenses = data.transactions?.expenses || [];
  const totalNet = expenses.reduce(
    (sum, exp) => sum + (exp.netAmount || exp.amount || 0),
    0
  );
  const totalVat = expenses.reduce((sum, exp) => sum + (exp.vatAmount || 0), 0);
  const totalGross = expenses.reduce(
    (sum, exp) => sum + (exp.grossAmount || exp.amount || 0),
    0
  );

  // Group by category
  const byCategory = {};
  expenses.forEach((exp) => {
    const cat = exp.category || "Uncategorized";
    if (!byCategory[cat]) byCategory[cat] = { items: [], total: 0, vat: 0 };
    byCategory[cat].items.push(exp);
    byCategory[cat].total += exp.netAmount || exp.amount || 0;
    byCategory[cat].vat += exp.vatAmount || 0;
  });

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Expense Breakdown ({expenses.length} items)
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => exportToCSV("expenses")}
        >
          Export CSV
        </Button>
      </Stack>

      {/* Category Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(byCategory)
          .sort((a, b) => b[1].total - a[1].total)
          .map(([cat, catData]) => (
            <Grid item xs={6} sm={4} md={3} key={cat}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {cat}
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {formatMoney(catData.total)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {catData.items.length} items
                </Typography>
              </Paper>
            </Grid>
          ))}
      </Grid>

      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Net
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                VAT
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Gross
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((exp, idx) => (
              <TableRow key={exp.id || idx} hover>
                <TableCell>{formatDate(exp.date)}</TableCell>
                <TableCell>{exp.description || exp.fileName || "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={exp.category || "Other"}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {formatMoney(exp.netAmount || exp.amount)}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(exp.vatAmount)}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {formatMoney(exp.grossAmount || exp.amount)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
              <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                TOTAL
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalNet)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalVat)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(totalGross)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>Verify:</strong> Total Expenses ({formatMoney(totalNet)}) should
        match reported expenses of {formatMoney(data.summary?.expenses)}
        {Math.abs(totalNet - (data.summary?.expenses || 0)) < 0.01 ? (
          <CheckIcon color="success" sx={{ ml: 1, verticalAlign: "middle" }} />
        ) : (
          <WarningIcon color="error" sx={{ ml: 1, verticalAlign: "middle" }} />
        )}
      </Alert>
    </Box>
  );
}

// VAT Tab Component
function VATTab({ data, formatMoney, theme }) {
  const vatCollected = data.vatSummary?.vatCollected || 0;
  const vatPaid = data.vatSummary?.vatPaid || 0;
  const vatLiability = data.vatSummary?.vatLiability || 0;

  // Calculate from transactions
  const calcVatCollected =
    data.transactions?.sales?.reduce(
      (sum, inv) => sum + (inv.vatAmount || 0),
      0
    ) || 0;
  const calcVatPaid =
    data.transactions?.expenses?.reduce(
      (sum, exp) => sum + (exp.vatAmount || 0),
      0
    ) || 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        VAT Reconciliation
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.05),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              VAT Collected (Output VAT)
            </Typography>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {formatMoney(vatCollected)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              From Invoices:
            </Typography>
            <Typography variant="body2">
              {formatMoney(calcVatCollected)}
            </Typography>
            {Math.abs(vatCollected - calcVatCollected) < 0.01 ? (
              <Chip
                label="✓ Matches"
                size="small"
                color="success"
                sx={{ mt: 1 }}
              />
            ) : (
              <Chip
                label="⚠ Discrepancy"
                size="small"
                color="error"
                sx={{ mt: 1 }}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              VAT Paid (Input VAT)
            </Typography>
            <Typography variant="h4" fontWeight={700} color="error.main">
              {formatMoney(vatPaid)}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              From Expenses:
            </Typography>
            <Typography variant="body2">{formatMoney(calcVatPaid)}</Typography>
            {Math.abs(vatPaid - calcVatPaid) < 0.01 ? (
              <Chip
                label="✓ Matches"
                size="small"
                color="success"
                sx={{ mt: 1 }}
              />
            ) : (
              <Chip
                label="⚠ Discrepancy"
                size="small"
                color="error"
                sx={{ mt: 1 }}
              />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(
                vatLiability >= 0
                  ? theme.palette.warning.main
                  : theme.palette.info.main,
                0.1
              ),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {vatLiability >= 0 ? "VAT Payable" : "VAT Refundable"}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              color={vatLiability >= 0 ? "warning.main" : "info.main"}
            >
              {formatMoney(Math.abs(vatLiability))}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Calculation:
            </Typography>
            <Typography variant="body2">
              {formatMoney(vatCollected)} - {formatMoney(vatPaid)} ={" "}
              {formatMoney(vatLiability)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="warning" sx={{ mt: 3 }}>
        <strong>Important:</strong> VAT returns are typically filed bi-monthly
        in Ireland. This is an annual summary. Ensure you've filed all VAT3
        returns for the year.
      </Alert>
    </Box>
  );
}

// Verification Tab Component
function VerificationTab({ verification, formatMoney, theme }) {
  if (!verification) return null;

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Data Verification Checks
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These automated checks ensure your financial data is consistent and
        accurate for Revenue submission.
      </Typography>

      <Stack spacing={2}>
        {verification.checks.map((check, idx) => (
          <Paper
            key={idx}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${
                check.passed
                  ? theme.palette.success.main
                  : theme.palette.error.main
              }`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {check.passed ? (
                <CheckIcon color="success" sx={{ fontSize: 32 }} />
              ) : (
                <ErrorIcon color="error" sx={{ fontSize: 32 }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight={600}>
                  {check.name}
                </Typography>
                {!check.passed && (
                  <Typography variant="body2" color="error">
                    Expected: {formatMoney(check.expected)} | Actual:{" "}
                    {formatMoney(check.actual)}
                  </Typography>
                )}
              </Box>
              <Chip
                label={check.passed ? "PASSED" : "FAILED"}
                color={check.passed ? "success" : "error"}
              />
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Alert
        severity={
          verification.passed === verification.total ? "success" : "error"
        }
        sx={{ mt: 3 }}
      >
        {verification.passed === verification.total ? (
          <>
            <strong>All checks passed!</strong> Your data is consistent and
            ready for Revenue submission.
          </>
        ) : (
          <>
            <strong>
              {verification.total - verification.passed} check(s) failed.
            </strong>{" "}
            Please review your data before submitting to Revenue.
          </>
        )}
      </Alert>
    </Box>
  );
}
